import { ScopeMode } from '@/client/custom-roles';
import { Group } from '@/client/groups';
import { useAccessScopeCount } from '@/hooks/useAccessScopeCount';
import React, { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

type AccessScopeCardProps = {
  mode: ScopeMode;
  firstName?: string;
  accountId?: string;
  userId?: string;
  scopeGroups?: Group[];
  onChangeAccessSettings?: () => void;
  children?: ReactNode;
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-padding); /* Figma 15px -> --medium-padding (16px) */
  padding: var(--default-padding-small);
  background-color: var(--white-main);
  border: 1px solid var(--gray-light);
  border-radius: var(--xsmall-border-radius);
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xxsmall-padding); /* Figma 3px -> --xxsmall-padding (4px) */
`;

const Title = styled.span`
  font-size: var(--small-font-size);
  font-weight: var(--bold-font-weight);
  line-height: var(--default-line-height);
`;

const Body = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--default-line-height);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--medium-padding);
`;

const AffectedCount = styled.span`
  font-size: var(--xsmall-font-size);
  font-weight: var(--regular-font-weight);
  line-height: var(--default-line-height);

  b {
    font-weight: var(--bold-font-weight);
  }
`;

const ChangeSettingsButton = styled.button`
  padding: 0 0 var(--semi-xsmall-padding);
  background: none;
  border: none;
  border-bottom: 2px solid var(--gray-buttom-border);
  color: var(--purple-dark);
  font-size: var(--xsmall-font-size);
  font-weight: var(--medium-font-weight);
  line-height: var(--xsmall-line-height);
  cursor: pointer;
`;

export const AccessScopeCard: React.FC<AccessScopeCardProps> = ({
  mode,
  firstName,
  accountId,
  userId,
  scopeGroups,
  onChangeAccessSettings,
  children,
}) => {
  const { t } = useTranslation();
  const affectedUserCount = useAccessScopeCount({
    accountId,
    mode,
    scopeGroups,
    userId,
  });
  const showFooter =
    affectedUserCount !== undefined || !!onChangeAccessSettings;

  return (
    <Card>
      <Heading>
        <Title>{t(`user.accessScope.${mode}.title`)}</Title>
        <Body>{t(`user.accessScope.${mode}.body`, { firstName })}</Body>
      </Heading>

      {children}

      {showFooter && (
        <Footer>
          {affectedUserCount === undefined ? (
            <span />
          ) : (
            <AffectedCount>
              <Trans
                i18nKey={`user.accessScope.${mode}.count`}
                count={affectedUserCount}
                values={{ count: affectedUserCount, firstName }}
                components={{ b: <b /> }}
              />
            </AffectedCount>
          )}

          {!!onChangeAccessSettings && (
            <ChangeSettingsButton
              type="button"
              onClick={onChangeAccessSettings}
            >
              {t('user.accessScope.changeSettings')}
            </ChangeSettingsButton>
          )}
        </Footer>
      )}
    </Card>
  );
};
